import React, {useState, Fragment, useEffect} from 'react'
import BulkLocationSelector from "./BulkLocationSelector";
import _ from "lodash"

import './ManageLocationsList.css'
import BulkLocationEditor, {emptyLocationPatch} from "../BulkLocationEditor";
import CategoryStore from "../../../../services/categories/CategoryStore";
import AccountsStore from "../../../../services/accounts/AccountsStore";

import YelpCategoryStore from "../../../../services/categories/YelpCategoryStore";
import BingCategoryStore from "../../../../services/categories/BingCategoryStore";
import AppleCategoryStore from "../../../../services/categories/AppleCategoryStore";

import client from '../../../../services/session/apiClient';


import {getLocationsAndGroups} from '../../../../services/locations/api';
import GoogleAttributesStore from "../../../../services/categories/GoogleAttributesStore";
import UsePageTitle from '../../../app/layout/UsePageTitle';
import PageTitle from '../../../../components/typography/PageTitle';
import RenderToPortal from '../../../../components/portals/RenderToPortal';
import { BACK_BUTTON } from '../../../app/navigator/navigatorPortalNames';
import {hasLocationScheduling} from "../../../app/site/featureAccessControl";

function ManageLocationsList({createMode, locationId, scheduledMode}) {
  const daysOfTheWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const singleLocationEditMode = !!locationId;
  const multiLocationEditMode = !createMode && !singleLocationEditMode;
  const locationScheduleMode = scheduledMode && hasLocationScheduling()

  if (locationScheduleMode) console.log("Bulk editor is in scheduled mode.");

  // Selected locations and groups
  // Looks like {location.1231412: true, group.123: false}
  const [selectedLocationsAndGroups, setSelectedLocationsAndGroups] = useState({});

  // Selected locations, and locations in selected groups
  // Looks like ["location.12341234", "location101010"]
  const [flattenedSelectedLocations, setFlattenedSelectedLocations] = useState((singleLocationEditMode ? [locationId] : []));

  const [locationPatch, setLocationPatch] = useState(emptyLocationPatch(createMode));

  const [startingLocationPatch, setStartingLocationPatch] = useState(emptyLocationPatch(createMode));

  // map of {locationId: location}, used for displaying pretty location names
  const [allLocations, setAllLocations] = useState([]);

  // map of {locationId: AccountServices}, used to hide/show fields depending on enabled services
  const [locationServicesMap, setLocationServicesMap] = useState(null);

  // map of {accountId: AccountServices}, used to hide/show fields depending on enabled services
  const [accountServicesMap, setAccountServicesMap] = useState(null);

  // Should the healthcare tab be displayed? True if one or more locations selected in Healthcare vertical
  const [includeHealthcareVertical, setIncludeHealthcareVertical] = useState(false);

  // Locations and groups for the location selector
  const [locationsAndGroups, setLocationsAndGroups] = useState(null);

  // we take all selected locations and if any of them have a service enabled
  // used by the location editor to hide/show fields depending on enabled services
  // if one or more locations has a service, it will show fields relevant to the service
  const defaultCombinedServicesMap = {
    chatExecListingManagement: false,
    localPages: false,
    listingManagement: false,
    bingListingManagement: false,
    appleListingManagement: false,
    yelpListingManagement: false,
    googleListingManagement: false,
    facebookListingManagement: false,
    yelpApi: false,
    socialPublishing: false,
    socialCommenting: false,
    sendiblePublishing: false
  };
  const [combinedLocationServicesMap, setCombinedLocationServicesMap] = useState(defaultCombinedServicesMap);

  // Prefetch categories
  // TODO - categories should be cached on the backend so this request is fast
  const [categoriesStore, setCategoriesStore] = useState(new CategoryStore());
  categoriesStore.loadCategories();

  // Prefetch accounts
  const [accountsStore, setAccountsStore] = useState(new AccountsStore());
  const accountsPromise = new Promise((resolve) => accountsStore.onLoadedListeners.push(resolve));
  accountsStore.loadAccounts();

  // TODO - categories should be cached on the backend so this request is fast
  const [yelpCategoriesStore, setYelpCategoriesStore] = useState(new YelpCategoryStore());
  yelpCategoriesStore.loadCategories();

  const [googleAttributesStore, setGoogleAttributesStore] = useState(new GoogleAttributesStore())

  // TODO - categories should be cached on the backend so this request is fast
  const [bingCategoriesStore, setBingCategoriesStore] = useState(new BingCategoryStore());
  bingCategoriesStore.loadCategories();

  // TODO - categories should be cached on the backend so this request is fast
  const [appleCategoriesStore, setAppleCategoriesStore] = useState(new AppleCategoryStore());
  appleCategoriesStore.loadCategories();

  // If we are in single location mode, start with loading indicators
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [lockLocationSelector, setLockLocationSelector] = useState(false);
  const [loadingLocationSelector, setLoadingLocationSelector] = useState(true);

  // When we change accounts, we need to hide/show certain tabs because certain features are enabled
  const selectAccount = (accountId) => {
    if (Object.keys(accountServicesMap || {}).length > 0) {
      if (!!accountServicesMap[accountId]) {
        setCombinedLocationServicesMap(accountServicesMap[accountId]);
      }
    }
  };

  const _setSelectedLocations = (selectedLocationsAndGroups, selected = flattenedSelectedLocations, isReset) => {
    setLoadingEditor(true);

    setSelectedLocationsAndGroups(selectedLocationsAndGroups);
    setFlattenedSelectedLocations(selected);

    const selectedLocations = selected;

    if (selectedLocations.length > 0) {
      //If we're resetting the changes or going back for more changes, just use exisiting services map.
      if (isReset) {
        setCombinedLocationServicesMap(combinedLocationServicesMap);
      } else {
        // Duplicate the first element in the array, doesn't affect the result, but JS reduce doesn't work with single item arrays
        const _combinedLocationServicesMap = [selectedLocations[0], ...selectedLocations].reduce((acc, selectedLocation) => {
          const locationServices = locationServicesMap ? locationServicesMap[selectedLocation] : {};
          try {
            return {
              chatExecListingManagement: acc.chatExecListingManagement || locationServices.chatExecListingManagement,
              localPages: acc.localPages || locationServices.localPages,
              listingManagement: acc.listingManagement || locationServices.listingManagement,
              bingListingManagement: acc.bingListingManagement || locationServices.bingListingManagement,
              appleListingManagement: acc.appleListingManagement || locationServices.appleListingManagement,
              yelpListingManagement: acc.yelpListingManagement || locationServices.yelpListingManagement,
              googleListingManagement: acc.googleListingManagement || locationServices.googleListingManagement,
              facebookListingManagement: acc.facebookListingManagement || locationServices.facebookListingManagement,
              yelpApi: acc.yelpApi || locationServices.yelpApi,
              socialPublishing: acc.socialPublishing || locationServices.socialPublishing,
              socialCommenting: acc.socialCommenting || locationServices.socialCommenting,
              sendiblePublishing: acc.sendiblePublishing || locationServices.sendiblePublishing,
            }
          } catch (e) {
            // QA has some messed up locations that are failing here. I've got this log in here to help debug these situations.
            // This could potentially cause some features to fail to display in the location editor, but only when location/accounts are not structured properly.
            console.warn("Failed to find account services for location: " + selectedLocation, e);

            return acc;
          }
        });
        setCombinedLocationServicesMap(_combinedLocationServicesMap);
      }
    } else {
      setCombinedLocationServicesMap(defaultCombinedServicesMap);
    }

    // Check to see if any of the selected locations are in the Healthcare vertical
    if (selectedLocations.length > 0) {
      client.post('/v5/locations/checkLocationsForVertical', {
        locationIds: selectedLocations,
        vertical: "Healthcare"
      })
      .then(response => {
        if (response.data.length > 0) {
          setIncludeHealthcareVertical(true)
        } else {
          setIncludeHealthcareVertical(false)
        }
      })
    }


    // If we have a single location we're editing, pull down all of its information so we can populate our forms
    if (selectedLocations.length === 1) {
      // Need to lock the location selector while we're loading locations
      // This prevents a race condition where a single location's details will overwrite multiple locations' details
      setLockLocationSelector(true);
      getLocation(selectedLocations[0])
        .then(location => {
          const patch = mapLocationToPatch(location);
          setLocationPatch(patch);
          setStartingLocationPatch(patch);
          setLoadingEditor(false);
          setLockLocationSelector(false);
        })
    } else {
      const patch = emptyLocationPatch(createMode)

      // Simulate loading and reset the patch
      Promise.resolve(patch).then(p => { setLocationPatch(p); setStartingLocationPatch(p); setLoadingEditor(false)})
    }
  };

  // Map a Chatmeter location to a Chatmeter location patch so we can populate the form
  const mapLocationToPatch = (response) => {
    const {location, brandMonitoring} = response;

    const maybeHours = !!location.listingManagementSpecs && !!location.listingManagementSpecs.hours;
    const days = maybeHours ? Object.keys(location.listingManagementSpecs.hours) : [];

    const hours = days.map(dayName => {
      const day = location.listingManagementSpecs.hours[dayName];
      const open24Hours = day.status === "Open" && !!day.blocks[0] && day.blocks[0].from === "0000" && day.blocks[0].to === "2400"
      return {
        name: dayName,
        isClosed: day.status === "Closed",
        open24Hours,
        hourBlocks: day.blocks.map(hourBlock => {
          return {
            openTime: hourBlock.from.slice(0,2) + ":" + hourBlock.from.slice(2,4),
            closeTime: hourBlock.to.slice(0,2) + ":" + hourBlock.to.slice(2,4),
          }
        })
      }
    });

    _.differenceWith(daysOfTheWeek, days.map(dayName => dayName), _.isEqual).map(dayName => {
      hours.push({name: dayName, hasNone:true, isClosed: false, open24Hours: false, hourBlocks: []})
    });


    const maybeSpecialHourPeriods = !!location.listingManagementSpecs && !!location.listingManagementSpecs.specialHoursPeriods;
    const specialHours = (maybeSpecialHourPeriods ? location.listingManagementSpecs.specialHoursPeriods : []).map(period => {
      return {
        date: period.specialStartDate.year + "-" + ("0" + period.specialStartDate.month).slice(-2) + "-" + ("0" + period.specialStartDate.day).slice(-2),
        isClosed: period.isClosed,
        open24Hours: period.is24hrs,
        openTime: !period.isClosed ? moment(period.openTime, ["h:mm A"]).format("HH:mm") : "",
        closeTime: !period.isClosed ? moment(period.closeTime, ["h:mm A"]).format("HH:mm") : ""
      }
    });


    const bingCategories = location.listingManagementSpecs.bingCategories.map(category => {
      // We do not know if we have the categoryId or not as it is not of any importance to our backend processing.
      return category.CategoryName
    });
    const primaryBingCategory = !!location.listingManagementSpecs.primaryBingCategory && location.listingManagementSpecs.primaryBingCategory.CategoryName;

    const startingLocationPatch = {
      ...locationPatch,
      ...location,
      accountId: location.accountId,
      businessName: location.businessName,
      clientLocationId: location.resellerLocId,
      emails: location.emails,
      locationType: location.locationType, // Because this is an on/off/null situation, and lodash merge doesn't overwrite values with undefined.. this needs to be null/true/false
      localPagesBrandId: location.localPagesBrandId,
      notes: location.notes,
      primaryCategory: location.primaryCategory,
      alternateWebsites: location.website.length > 1 ? location.website.slice(1, location.website.length) : [],
      primaryWebsite: location.website.length > 0 ? location.website[0] : undefined,
      website: undefined, // Patch separates primary/alternate websites
      alternatePhoneNumbers: location.phoneNumber.length > 1 ? location.phoneNumber.slice(1, location.phoneNumber.length) : [],
      primaryPhoneNumber: location.phoneNumber.length > 0 ? location.phoneNumber[0] : undefined,
      phoneNumber: undefined, // Patch separates primary/alternate websites
      brandMonitoring: {
        listingMonitoring: !!brandMonitoring && !!brandMonitoring.listingMonitoring ? brandMonitoring.listingMonitoring : []
      },
      businessAddress: location.businessAddress,
      additionalCategories: location.additionalCategories || [],
      listingManagementSpecs: (typeof location.listingManagementSpecs === 'object' && Object.keys(location.listingManagementSpecs).length > 0) ? {
        ...location.listingManagementSpecs,
        primaryAppleCategory: !!location.listingManagementSpecs.primaryAppleCategory && location.listingManagementSpecs.primaryAppleCategory.categoryId,
        primaryBingCategory: primaryBingCategory,
        yearEstablished: location.listingManagementSpecs.yearsInBusiness,
        appleCategories: ((!!location.listingManagementSpecs
            && !!location.listingManagementSpecs.appleCategories
            && location.listingManagementSpecs.appleCategories.map(category => category.categoryId)) || []),
        bingCategories: bingCategories,
        healthCareManagement: (location.healthCareManagement || {
          nationalProviderIdentifier: undefined,
          specialties: undefined,
          conditionsTreated: undefined,
          acceptingNewPatients: null
        }),
        hours,
        specialHours
      } : emptyLocationPatch(createMode).listingManagementSpecs,
      yelpListingManagementSpecs: {
        ...((typeof location.yelpListingManagementSpecs === 'object' && Object.keys(location.yelpListingManagementSpecs).length > 0) ? location.yelpListingManagementSpecs : emptyLocationPatch(createMode).yelpListingManagementSpecs),
        categories: !!location.listingManagementSpecs && !!location.listingManagementSpecs.yelpCategories && location.listingManagementSpecs.yelpCategories || [],
        images: !!location.listingManagementSpecs && !!location.listingManagementSpecs.yelpImages && location.listingManagementSpecs.yelpImages || []
      },
      googleListingManagementSpecs: {
        ...((typeof location.googleListingManagementSpecs === 'object' && Object.keys(location.googleListingManagementSpecs).length > 0) ? location.googleListingManagementSpecs : emptyLocationPatch(createMode).googleListingManagementSpecs),
        googleCategories: (!!location.listingManagementSpecs && !!location.listingManagementSpecs.googleCategories) ? location.listingManagementSpecs.googleCategories : [],
      },
      facebookListingManagementSpecs: (typeof location.facebookListingManagementSpecs === 'object' && Object.keys(location.facebookListingManagementSpecs).length > 0) ? location.facebookListingManagementSpecs : emptyLocationPatch(createMode).facebookListingManagementSpecs,
    };

    return startingLocationPatch;
  };

  const getLocation = (locationId) => client.get('/v5/locations/syncAndGet/' + locationId + "?raw=true").then(resp => resp.data)

  // Grab a list of all locations with their accounts
  // Combine two for service map

  const getAccountServices = () => {
    return client.get("/v5/accounts")
        .then(resp => resp.data.accounts)
        .then(accounts => {
          const accountServices = {};
          accounts.forEach(account => {
            accountServices[account.id] = account.services;
          })
          return accountServices;
        })
  }

  const getAccountsWithLocations = (locationIds) => {
    return client.post("/v5/listAccountServicesForAccounts", {locationIds})
        .then(resp => resp.data)
  }

  const getLocations = ({searchQuery = ""}) => getLocationsAndGroups({ search: searchQuery, includeGroupLocationIds: true })
      .then(locationsAndGroups => {
        // Account location ids come with the legacy- prefix, need to remove that
        return {
          ...locationsAndGroups,
          accounts: locationsAndGroups.accounts.map(account => {
            return {
              ...account,
              locations: account.locations.map(locationId => locationId.replace("legacy-", ""))
            }
          })
        };
      });

  useEffect(() => {
    // Single location edit mode
    if (singleLocationEditMode && !createMode) {

      setLoadingEditor(true);
      // Grab a list of all accounts with services
      const accountServicesPromise = getAccountServices()

      // Grab the details of the location being edited
      const locationsPromise = getLocation(flattenedSelectedLocations[0])
          .then(mapLocationToPatch)

      // need this if user has access to locations not associated with the user's account
      const accountLocationsPromise = getAccountsWithLocations(flattenedSelectedLocations)

      Promise.all([
        accountServicesPromise,
        locationsPromise,
        accountLocationsPromise
      ]).then(([accountServices, startingLocationPatch, accountsResponse]) => {
        const accountForLocation = accountsResponse[0].account;
        accountServices[accountForLocation.accountId]  = accountForLocation.services;

        setAccountServicesMap(accountServices);
        setLocationPatch(startingLocationPatch);
        setStartingLocationPatch(startingLocationPatch);
        setCombinedLocationServicesMap(accountServices[startingLocationPatch.accountId])
        setLoadingEditor(false);
      })
    } else if (createMode) {
      // Create Mode
      setLoadingEditor(true);
      // Grab a list of all accounts with services
      const accountServicesPromise = getAccountServices()

      accountServicesPromise.then(accountServices => {
            setAccountServicesMap(accountServices);

            if (Object.keys(accountServices).length === 1) {
              const onlyAccountId = Object.keys(accountServices)[0]
              // If there is only one account, the account selector is hidden
              // We need to display tabs for this account
              setCombinedLocationServicesMap(accountServices[onlyAccountId])

              // We also need to set the accountId on the locationPatch

              setLocationPatch({...locationPatch, accountId:onlyAccountId });
              setStartingLocationPatch({...locationPatch, accountId:onlyAccountId });
            }

            setLoadingEditor(false);
          })

    } else {
      // Bulk Edit Mode
      setLoadingLocationSelector(true);
      // Get first page of locations and groups for the selector
      const locationsAndGroupsPromise = getLocations({})

      locationsAndGroupsPromise.then(locationsAndGroups => {
        const allLocationIds = locationsAndGroups.all[0].locations;

        getAccountsWithLocations(allLocationIds)
            .then(accountsWithLocations => {
              const locationServices = {};
              const accountServices = {};
              accountsWithLocations.forEach(accountWithLocations => {
                const account = accountWithLocations.account
                accountServices[account.accountId] = account.services;
                accountWithLocations.locationIds.forEach(locationId => {
                  locationServices[locationId] = account.services;
                })
              })

              setLocationsAndGroups(locationsAndGroups);
              setLocationServicesMap(locationServices);
              setAccountServicesMap(accountServices);
              setLoadingLocationSelector(false);
            })
      })
    }
  }, []);

  const searchLocationsAndGroups = (searchQuery) => {
    setLoadingLocationSelector(true);
    getLocations({searchQuery: searchQuery})
        .then(locationsAndGroups => {
            setLocationsAndGroups(locationsAndGroups)
            setLoadingLocationSelector(false);
        })
  }

  const _pageName = () => {
    if (singleLocationEditMode) {
      return "Edit Location"
    } else if (createMode) {
      return "Add Location"
    } else if (locationScheduleMode) {
      return "Schedule Location Updates"
    } else {
      return "Edit Multiple Locations"
    }
  };
  const pageName = _pageName();

  return <div>
    <UsePageTitle>
      <PageTitle>
        {pageName}
      </PageTitle>
    </UsePageTitle>
    {/* TODO - Figure out why UseBackButton isn't getting the correct context here */}
    <RenderToPortal id={BACK_BUTTON}>
      <a href="/#/admin/v2/locations" className="floating-commands btn btn-outline-secondary card-spacer-right">
        <i className="far fa-arrow-left "></i> Back
      </a>
    </RenderToPortal>
    <div className="d-flex flex-row">
      {!createMode && !singleLocationEditMode && (
        <div className="card card-spacer-right">
          <div className="card-body card-only-child">
            <div className="py-0 pl-0 manage-locations-list-selector">
              <BulkLocationSelector
                loadingLocationSelector={loadingLocationSelector}
                lockLocationSelector={lockLocationSelector}
                onSelectedLocations={_setSelectedLocations}
                onSearch={searchLocationsAndGroups}
                locationsAndGroups={locationsAndGroups}
                selectedLocationsAndGroups={selectedLocationsAndGroups}
                flattenedSelectedLocations={flattenedSelectedLocations}
              />
            </div>
          </div>
        </div>)
      }
      <div className="manage-locations-list-location-editor">
        {(flattenedSelectedLocations.length > 0 || createMode) &&
        (loadingEditor === true ?
        <div className='card'>
          <div className="card-body card-only-child">
            <div className='mb-2'>
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}} />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
            </div>
            <div className='mb-2'>
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
            </div>
            <div className='mb-2'>
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
              <div className='skeleton-text-lg' style={{"maxWidth": "none"}}  />
            </div>
          </div>
        </div> : <BulkLocationEditor
          createMode={createMode}
          multiLocationEditMode={multiLocationEditMode}
          locationPatch={locationPatch}
          startingLocationPatch={startingLocationPatch}
          setLocationPatch={setLocationPatch}
          selectAccount={selectAccount}
          onSelectedLocations={_setSelectedLocations}
          combinedLocationServicesMap={combinedLocationServicesMap}
          accountsStore={accountsStore}
          includeHealthcareVertical={includeHealthcareVertical}
          yelpCategoriesStore={yelpCategoriesStore}
          googleAttributesStore={googleAttributesStore}
          bingCategoriesStore={bingCategoriesStore}
          appleCategoriesStore={appleCategoriesStore}
          categoriesStore={categoriesStore}
          selectedLocations={flattenedSelectedLocations}
          selectedLocationsAndGroups={selectedLocationsAndGroups}
          allLocations={allLocations}
          locationScheduleMode={locationScheduleMode}/>
        )}
      </div>

    </div>
  </div>
}

export default ManageLocationsList
